import React, { useEffect, useState, useRef } from 'react';
import ICAL from 'ical.js';
import AdminSidebar from '../components/AdminSidebar';
import LoginForm from '../components/LoginForm';
import './AdminPage.css';
import { clearAuth, getAuthRole, setAuthRole, subscribeAuthRole } from '../utils/auth';

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const diffDaysUtc = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((utcEnd - utcStart) / (1000 * 60 * 60 * 24));
};

const parseDate = (str) => {
  const [day, month, year] = String(str || '').split('/');
  return new Date(`${year}-${month}-${day}`);
};

const capitalizeFirst = (value) => {
  if (!value) {
    return '';
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const splitPeriodByMonth = (period) => {
  const segments = [];
  const meta = { ...period };
  delete meta.start;
  delete meta.end;
  let segmentStart = new Date(period.start);

  while (segmentStart < period.end) {
    const nextMonthStart = new Date(segmentStart.getFullYear(), segmentStart.getMonth() + 1, 1);
    const segmentEnd = nextMonthStart < period.end ? nextMonthStart : new Date(period.end);
    const displayEnd = new Date(segmentEnd);

    segments.push({
      ...meta,
      monthKey: `${segmentStart.getFullYear()}-${String(segmentStart.getMonth() + 1).padStart(2, '0')}`,
      monthDate: new Date(segmentStart.getFullYear(), segmentStart.getMonth(), 1),
      start: new Date(segmentStart),
      end: segmentEnd,
      endDisplay: displayEnd,
      nights: diffDaysUtc(segmentStart, segmentEnd)
    });

    segmentStart = segmentEnd;
  }

  return segments;
};

const buildMonthlyAvailabilityData = (periods) => {
  const monthsMap = new Map();

  periods.forEach((period) => {
    splitPeriodByMonth(period).forEach((segment) => {
      if (!monthsMap.has(segment.monthKey)) {
        monthsMap.set(segment.monthKey, {
          monthKey: segment.monthKey,
          monthDate: segment.monthDate,
          periods: [],
          nights: 0
        });
      }

      const monthEntry = monthsMap.get(segment.monthKey);
      monthEntry.periods.push(segment);
      monthEntry.nights += segment.nights;
    });
  });

  return Array.from(monthsMap.values()).sort((left, right) => left.monthDate - right.monthDate);
};

const getRollingWindowBounds = () => {
  const today = normalizeDate(new Date());
  const windowEndExclusive = normalizeDate(
    new Date(today.getFullYear(), today.getMonth() + 12, today.getDate())
  );

  return { today, windowEndExclusive };
};

const clipPeriodsToWindow = (periods, windowStart, windowEndExclusive) => {
  return periods
    .map((period) => {
      const start = normalizeDate(period.start);
      const end = normalizeDate(period.end);
      const clippedStart = start < windowStart ? windowStart : start;
      const clippedEnd = end > windowEndExclusive ? windowEndExclusive : end;

      if (clippedEnd <= clippedStart) {
        return null;
      }

      return {
        ...period,
        start: new Date(clippedStart),
        end: new Date(clippedEnd),
      };
    })
    .filter(Boolean);
};

const getAvailablePeriods = (reservedRanges) => {
  const { today, windowEndExclusive } = getRollingWindowBounds();

  const mergedRanges = reservedRanges
    .map((range) => ({
      start: normalizeDate(range.start),
      end: normalizeDate(range.end)
    }))
    .filter((range) => range.end > today && range.start < windowEndExclusive)
    .sort((left, right) => left.start - right.start)
    .reduce((accumulator, currentRange) => {
      if (accumulator.length === 0) {
        accumulator.push({ ...currentRange });
        return accumulator;
      }

      const lastRange = accumulator[accumulator.length - 1];
      if (currentRange.start <= lastRange.end) {
        if (currentRange.end > lastRange.end) {
          lastRange.end = currentRange.end;
        }
      } else {
        accumulator.push({ ...currentRange });
      }

      return accumulator;
    }, []);

  const availablePeriods = [];
  let cursor = today;

  mergedRanges.forEach((range) => {
    const boundedStart = range.start > windowEndExclusive ? windowEndExclusive : range.start;
    const boundedEnd = range.end > windowEndExclusive ? windowEndExclusive : range.end;

    if (boundedStart > cursor) {
      availablePeriods.push({
        start: new Date(cursor),
        end: new Date(boundedStart)
      });
    }

    if (boundedEnd > cursor) {
      cursor = new Date(boundedEnd);
    }
  });

  if (cursor < windowEndExclusive) {
    availablePeriods.push({
      start: new Date(cursor),
      end: new Date(windowEndExclusive)
    });
  }

  return availablePeriods.map((period) => {
    const endDisplay = new Date(period.end);

    return {
      ...period,
      endDisplay,
      nights: diffDaysUtc(period.start, period.end)
    };
  });
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getAuthRole() === 'admin';
  });
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [reservedPeriods, setReservedPeriods] = useState([]);
  const [lostNightPeriods, setLostNightPeriods] = useState([]);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(true);
  const [periodsError, setPeriodsError] = useState('');
  const [tarifs, setTarifs] = useState([]);
  const [selectedLang, setSelectedLang] = useState('fr');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [monthCopyFeedback, setMonthCopyFeedback] = useState({});
  const [showMonthlyView, setShowMonthlyView] = useState(true);
  const [showReservedPeriods, setShowReservedPeriods] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [selectedPostMonths, setSelectedPostMonths] = useState(() => new Set());
  const [postCopyFeedback, setPostCopyFeedback] = useState('');
  const [requestStartDate, setRequestStartDate] = useState('');
  const [requestEndDate, setRequestEndDate] = useState('');
  const [requestCheckMessage, setRequestCheckMessage] = useState('');
  const [requestCheckType, setRequestCheckType] = useState('');

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  useEffect(() => {
    return subscribeAuthRole((role) => {
      setIsAuthenticated(role === 'admin');
    });
  }, []);

  const availabilityI18n = {
    fr: { flag: 'FR', locale: 'fr-FR', title: 'Disponibilités restantes', window: '12 mois glissants', loading: 'Chargement des disponibilités...', empty: 'Aucune période disponible sur les 12 prochains mois.', from: 'Du', to: 'au', nights: 'nuits disponibles', copy: 'Copier', copied: 'Copié', monthly: 'Mois par mois', start: 'Début', end: 'Fin', check: 'Tester', checkOk: 'Cette demande est disponible.', checkKo: 'Cette demande n\'est pas disponible.', checkInvalid: 'Renseignez une date de début et une date de fin valides.' },
    it: { flag: 'IT', locale: 'it-IT', title: 'Disponibilità rimanenti', window: '12 mesi scorrevoli', loading: 'Caricamento disponibilità...', empty: 'Nessun periodo disponibile nei prossimi 12 mesi.', from: 'Dal', to: 'al', nights: 'notti disponibili', copy: 'Copia', copied: 'Copiato', monthly: 'Mese per mese', start: 'Inizio', end: 'Fine', check: 'Verifica', checkOk: 'Questa richiesta è disponibile.', checkKo: 'Questa richiesta non è disponibile.', checkInvalid: 'Inserire date di inizio e fine valide.' },
    en: { flag: 'EN', locale: 'en-GB', title: 'Remaining availability', window: 'rolling 12 months', loading: 'Loading availability...', empty: 'No periods available in the next 12 months.', from: 'From', to: 'to', nights: 'nights available', copy: 'Copy', copied: 'Copied', monthly: 'Month by month', start: 'Start', end: 'End', check: 'Check', checkOk: 'This request is available.', checkKo: 'This request is not available.', checkInvalid: 'Please enter valid start and end dates.' },
    de: { flag: 'DE', locale: 'de-DE', title: 'Verfugbarkeiten', window: 'rollierende 12 Monate', loading: 'Verfugbarkeit wird geladen...', empty: 'Keine verfugbaren Zeitraume in den nachsten 12 Monaten.', from: 'Von', to: 'bis', nights: 'Nachte verfugbar', copy: 'Kopieren', copied: 'Kopiert', monthly: 'Monat fur Monat', start: 'Beginn', end: 'Ende', check: 'Prufen', checkOk: 'Diese Anfrage ist verfugbar.', checkKo: 'Diese Anfrage ist nicht verfugbar.', checkInvalid: 'Bitte gultige Start- und Enddaten eingeben.' },
    es: { flag: 'ES', locale: 'es-ES', title: 'Disponibilidad restante', window: '12 meses móviles', loading: 'Cargando disponibilidad...', empty: 'No hay periodos disponibles en los próximos 12 meses.', from: 'Del', to: 'al', nights: 'noches disponibles', copy: 'Copiar', copied: 'Copiado', monthly: 'Mes a mes', start: 'Inicio', end: 'Fin', check: 'Verificar', checkOk: 'Esta solicitud está disponible.', checkKo: 'Esta solicitud no está disponible.', checkInvalid: 'Introduce fechas de inicio y fin válidas.' },
    pt: { flag: 'PT', locale: 'pt-PT', title: 'Disponibilidades restantes', window: '12 meses móveis', loading: 'A carregar disponibilidades...', empty: 'Nao ha periodos disponiveis nos proximos 12 meses.', from: 'De', to: 'a', nights: 'noites disponiveis', copy: 'Copiar', copied: 'Copiado', monthly: 'Mes a mes', start: 'Inicio', end: 'Fim', check: 'Verificar', checkOk: 'Este pedido está disponível.', checkKo: 'Este pedido não está disponível.', checkInvalid: 'Preencha datas de início e fim válidas.' },
    ru: { flag: 'RU', locale: 'ru-RU', title: 'Ostavshayasya dostupnost', window: '12 mesyatsev', loading: 'Zagruzka dostupnosti...', empty: 'Net dostupnyh periodov na sleduyushchie 12 mesyatsev.', from: 'S', to: 'po', nights: 'dostupnyh nochey', copy: 'Kopirovat', copied: 'Skopirovano', monthly: 'Po mesyacam', start: 'Nachalo', end: 'Konec', check: 'Proverit', checkOk: 'Etot zapros dostupen.', checkKo: 'Etot zapros nedostupen.', checkInvalid: 'Vvedite korrektnye daty nachala i okonchaniya.' }
  };

  const ui = availabilityI18n[selectedLang] || availabilityI18n.fr;

  const formatLocalizedDate = (date) => {
    return new Intl.DateTimeFormat(ui.locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const formatLocalizedMonth = (date) => {
    return new Intl.DateTimeFormat(ui.locale, {
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const getTarifForDate = (date) => {
    return tarifs.find((tarif) => date >= tarif.start && date <= tarif.end);
  };

  const availablePeriodsWithMeta = availablePeriods.map((period) => {
    if (!tarifs.length) {
      return period;
    }

    const applicableTarif = getTarifForDate(period.start);
    const minNuits = applicableTarif?.minNuits || 1;
    const isOpti = period.nights > 1 && period.nights < minNuits;

    return {
      ...period,
      isOpti,
      minNuits,
    };
  });

  const monthlyAvailability = buildMonthlyAvailabilityData(availablePeriodsWithMeta);

  const { today: windowStart, windowEndExclusive } = getRollingWindowBounds();
  const reservedPeriodsClipped = clipPeriodsToWindow(reservedPeriods, windowStart, windowEndExclusive);
  const lostNightPeriodsClipped = clipPeriodsToWindow(lostNightPeriods, windowStart, windowEndExclusive);

  const availabilityTimelinePeriods = [
    ...availablePeriodsWithMeta.map((period) => ({
      ...period,
      isReserved: false,
    })),
    ...reservedPeriodsClipped.map((period) => ({
      ...period,
      endDisplay: new Date(period.end),
      isReserved: true,
    })),
    ...lostNightPeriodsClipped.map((period) => ({
      ...period,
      endDisplay: new Date(period.endDisplay || period.end),
      isReserved: true,
      sourceId: 'lostnight',
      sourceLabel: 'Perdu',
      isLostNight: true,
    })),
  ].sort((left, right) => left.start - right.start || left.end - right.end);

  const monthlyTimeline = buildMonthlyAvailabilityData(availabilityTimelinePeriods);

  useEffect(() => {
    if (!isPostModalOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsPostModalOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isPostModalOpen]);

  const buildPostText = () => {
    const intro =
      "Pressez vous si vous voulez passer quelques jours au paradis, il ne reste plus trop de créneaux disponibles pour cet été !";
    const selectedMonths = monthlyAvailability.filter((month) => selectedPostMonths.has(month.monthKey));

    if (selectedMonths.length === 0) {
      return intro;
    }

    const sections = selectedMonths.map((month) => {
      const header = capitalizeFirst(formatLocalizedMonth(month.monthDate));
      const lines = month.periods.map((period) => {
        const promoSuffix = period.isOpti ? ' (-10%)' : '';
        return `${ui.from} ${formatLocalizedDate(period.start)} ${ui.to} ${formatLocalizedDate(period.endDisplay)}${promoSuffix}`;
      });

      return `${header}\n${lines.join('\n')}`;
    });

    return [intro, '', ...sections].join('\n\n');
  };

  const handleCopyPostText = async () => {
    try {
      await navigator.clipboard.writeText(buildPostText());
      setPostCopyFeedback(ui.copied);
      setTimeout(() => setPostCopyFeedback(''), 1500);
    } catch (error) {
      console.error('Copie impossible', error);
    }
  };

  const togglePostMonth = (monthKey) => {
    setSelectedPostMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  };

  const buildAvailabilityText = () => {
    if (availablePeriods.length === 0) {
      return `${ui.title} (${ui.window})\n${ui.empty}`;
    }

    const lines = availablePeriods.map((period) => {
      return `${ui.from} ${formatLocalizedDate(period.start)} ${ui.to} ${formatLocalizedDate(period.endDisplay)}`;
    });

    return `${ui.title} (${ui.window})\n${lines.join('\n')}`;
  };

  const handleCopyAvailability = async () => {
    try {
      await navigator.clipboard.writeText(buildAvailabilityText());
      setCopyFeedback(ui.copied);
      setTimeout(() => setCopyFeedback(''), 1500);
    } catch (error) {
      console.error('Copie impossible', error);
    }
  };

  const buildMonthAvailabilityText = (month) => {
    const header = `${formatLocalizedMonth(month.monthDate)}`;
    const lines = month.periods.map((period) => {
      return `${ui.from} ${formatLocalizedDate(period.start)} ${ui.to} ${formatLocalizedDate(period.endDisplay)}`;
    });

    return `${header}\n${lines.join('\n')}`;
  };

  const handleCopyMonthAvailability = async (month) => {
    try {
      await navigator.clipboard.writeText(buildMonthAvailabilityText(month));
      setMonthCopyFeedback((prev) => ({ ...prev, [month.monthKey]: ui.copied }));
      setTimeout(() => {
        setMonthCopyFeedback((prev) => {
          const next = { ...prev };
          delete next[month.monthKey];
          return next;
        });
      }, 1500);
    } catch (error) {
      console.error('Copie mensuelle impossible', error);
    }
  };

  const checkRequestedRange = () => {
    if (!requestStartDate || !requestEndDate) {
      setRequestCheckType('error');
      setRequestCheckMessage(ui.checkInvalid);
      return;
    }

    const start = normalizeDate(new Date(requestStartDate));
    const end = normalizeDate(new Date(requestEndDate));

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      setRequestCheckType('error');
      setRequestCheckMessage(ui.checkInvalid);
      return;
    }

    const isAvailable = availablePeriods.some((period) => start >= period.start && end <= period.end);
    setRequestCheckType(isAvailable ? 'success' : 'error');
    setRequestCheckMessage(isAvailable ? ui.checkOk : ui.checkKo);
  };

  useEffect(() => {
    const fetchAvailablePeriods = async () => {
      setIsLoadingPeriods(true);
      setPeriodsError('');

      try {
        const response = await fetch('/calendar-proxied-for-cors.php?withSources=1');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        const jcalData = ICAL.parse(text);
        const calendar = new ICAL.Component(jcalData);
        const events = calendar.getAllSubcomponents('vevent');
        const reservations = events.map((event) => {
          const reservation = new ICAL.Event(event);

          const sourceId =
            reservation.component.getFirstPropertyValue('x-villa-source-id') ||
            reservation.component.getFirstPropertyValue('X-VILLA-SOURCE-ID') ||
            null;
          const sourceLabel =
            reservation.component.getFirstPropertyValue('x-villa-source') ||
            reservation.component.getFirstPropertyValue('X-VILLA-SOURCE') ||
            null;

          return {
            start: reservation.startDate.toJSDate(),
            end: reservation.endDate.toJSDate(),
            sourceId: typeof sourceId === 'string' && sourceId.trim() ? sourceId.trim().toLowerCase() : null,
            sourceLabel: typeof sourceLabel === 'string' && sourceLabel.trim() ? sourceLabel.trim() : null
          };
        });

        const computedAvailable = getAvailablePeriods(reservations);
        const lost = computedAvailable
          .filter((period) => period.nights === 1)
          .map((period) => ({
            ...period,
            isReserved: true,
            isLostNight: true,
            sourceId: 'lostnight',
            sourceLabel: 'Perdu',
          }));

        setReservedPeriods(reservations);
        setLostNightPeriods(lost);
        setAvailablePeriods(computedAvailable.filter((period) => period.nights !== 1));
      } catch (error) {
        console.error('Erreur lors du chargement des disponibilités admin :', error);
        setPeriodsError('Impossible de charger les disponibilités pour le moment.');
      } finally {
        setIsLoadingPeriods(false);
      }
    };

    if (isAuthenticated) {
      fetchAvailablePeriods();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchTarifs = async () => {
      try {
        const res = await fetch(`/json/tarifs.json?v=${Date.now()}`);
        const data = await res.json();
        const cleaned = data.map((item) => ({
          start: parseDate(item['Date de début']),
          end: parseDate(item['Date de fin']),
          prix: Number(item['Prix par nuit']),
          minNuits: Number(item['Nuits minimum']),
        }));
        setTarifs(cleaned);
      } catch (err) {
        console.error('Erreur lors du chargement des tarifs admin :', err);
      }
    };

    if (isAuthenticated) {
      fetchTarifs();
    }
  }, [isAuthenticated]);

  const handleLogin = (role) => {
    if (role !== 'admin') {
      return;
    }
    setAuthRole('admin');
  };

  const handleLogout = () => {
    clearAuth();
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="site-shell admin-shell">
      <AdminSidebar
        onLogout={handleLogout}
        sitePath="/it/affitto-villa-appartamento-sardegna-san-teodoro"
        role="admin"
      />
      <main className="site-shell-content admin-content">
        <div className="admin-panel">
          <h2>Espace administrateur</h2>
          <p>Connexion active sur ce navigateur.</p>

          <section className="admin-section">
            <div className="admin-section-header">
              <h3>{ui.title} ({ui.window})</h3>
              <div className="admin-actions-inline">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={() => setShowReservedPeriods((value) => !value)}
                >
                  {showReservedPeriods ? 'Masquer réservations' : 'Voir réservations'}
                </button>
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={() => {
                    setSelectedPostMonths(new Set());
                    setPostCopyFeedback('');
                    setIsPostModalOpen(true);
                  }}
                >
                  Texte post
                </button>
                <button
                  type="button"
                  className="admin-month-toggle"
                  onClick={() => setShowMonthlyView((value) => !value)}
                  aria-label={ui.monthly}
                  title={ui.monthly}
                >
                  {showMonthlyView ? '−' : '+'}
                </button>
                <button type="button" className="admin-copy-btn" onClick={handleCopyAvailability}>
                  {copyFeedback || ui.copy}
                </button>
              </div>
            </div>

            <div className="admin-lang-switcher">
              {Object.keys(availabilityI18n).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`admin-lang-btn ${selectedLang === lang ? 'active' : ''}`}
                  onClick={() => setSelectedLang(lang)}
                >
                  {availabilityI18n[lang].flag}
                </button>
              ))}
            </div>

            <div className="admin-request-checker">
              <div className="admin-request-fields">
                <label onClick={() => { try { startDateRef.current?.showPicker(); } catch (_) {} }}>
                  {ui.start}
                  <input
                    ref={startDateRef}
                    type="date"
                    value={requestStartDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRequestStartDate(val);
                      if (val) {
                        const d = new Date(val);
                        d.setDate(d.getDate() + 4);
                        setRequestEndDate(d.toISOString().split('T')[0]);
                      }
                    }}
                  />
                </label>
                <label onClick={() => { try { endDateRef.current?.showPicker(); } catch (_) {} }}>
                  {ui.end}
                  <input
                    ref={endDateRef}
                    type="date"
                    value={requestEndDate}
                    onChange={(e) => setRequestEndDate(e.target.value)}
                  />
                </label>
                <button type="button" className="admin-check-btn" onClick={checkRequestedRange}>
                  {ui.check}
                </button>
              </div>
              {requestCheckMessage && (
                <p className={`admin-request-result ${requestCheckType}`}>{requestCheckMessage}</p>
              )}
            </div>

            {isLoadingPeriods && <p>{ui.loading}</p>}

            {!isLoadingPeriods && periodsError && (
              <p className="admin-error">{periodsError}</p>
            )}

            {!isLoadingPeriods && !periodsError && availablePeriods.length === 0 && (
              <p>{ui.empty}</p>
            )}

            {!isLoadingPeriods && !periodsError && availablePeriods.length > 0 && (
              <>
                {showMonthlyView ? (
                  <div className="admin-month-groups">
                    {(showReservedPeriods ? monthlyTimeline : monthlyAvailability).map((month) => (
                      <section key={month.monthKey} className="admin-month-group">
                        <div className="admin-month-header">
                          <h4>{capitalizeFirst(formatLocalizedMonth(month.monthDate))}</h4>
                          {!showReservedPeriods && (
                            <button
                              type="button"
                              className="admin-month-copy-btn"
                              onClick={() => handleCopyMonthAvailability(month)}
                            >
                              {monthCopyFeedback[month.monthKey] || ui.copy}
                            </button>
                          )}
                        </div>
                        <ul className="admin-period-list">
                          {month.periods.map((period) => (
                            <li
                              key={`${period.start.toISOString()}-${period.end.toISOString()}-${month.monthKey}-${period.isReserved ? (period.sourceId || 'reserved') : 'free'}`}
                              className={
                                period.isReserved || period.isOpti
                                  ? 'admin-period-item admin-period-item--with-badge'
                                  : 'admin-period-item'
                              }
                            >
                              <strong>
                                {ui.from} {formatLocalizedDate(period.start)} {ui.to} {formatLocalizedDate(period.endDisplay)}
                              </strong>
                              {period.isLostNight && (
                                <span className="reservation-badge reservation-badge--lostnight">Perdu</span>
                              )}
                              {!period.isReserved && period.isOpti && (
                                <>
                                  <span className="reservation-badge reservation-badge--shortstay">Opti</span>
                                  <span className="reservation-badge reservation-badge--promo">-10%</span>
                                </>
                              )}
                              {period.isReserved && !period.isLostNight && period.sourceLabel && (
                                <span
                                  className={
                                    period.sourceId
                                      ? `reservation-badge reservation-badge--${period.sourceId}`
                                      : 'reservation-badge'
                                  }
                                >
                                  {period.sourceLabel}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                ) : (
                  <ul className="admin-period-list">
                    {(showReservedPeriods ? availabilityTimelinePeriods : availablePeriodsWithMeta).map((period) => (
                      <li
                        key={`${period.start.toISOString()}-${period.end.toISOString()}-${period.isReserved ? (period.sourceId || 'reserved') : 'free'}`}
                        className={
                          period.isReserved || period.isOpti
                            ? 'admin-period-item admin-period-item--with-badge'
                            : 'admin-period-item'
                        }
                      >
                        <strong>
                          {ui.from} {formatLocalizedDate(period.start)} {ui.to} {formatLocalizedDate(period.endDisplay)}
                        </strong>
                        {period.isLostNight && (
                          <span className="reservation-badge reservation-badge--lostnight">Perdu</span>
                        )}
                        {!period.isReserved && period.isOpti && (
                          <>
                            <span className="reservation-badge reservation-badge--shortstay">Opti</span>
                            <span className="reservation-badge reservation-badge--promo">-10%</span>
                          </>
                        )}
                        {period.isReserved && !period.isLostNight && period.sourceLabel && (
                          <span
                            className={
                              period.sourceId
                                ? `reservation-badge reservation-badge--${period.sourceId}`
                                : 'reservation-badge'
                            }
                          >
                            {period.sourceLabel}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      {isPostModalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setIsPostModalOpen(false)}>
          <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="admin-modal-close"
              onClick={() => setIsPostModalOpen(false)}
              aria-label="Fermer"
            >
              ×
            </button>
            <h3>Texte de post</h3>
            <p>Sélectionnez les mois à inclure, puis copiez le texte.</p>

            <div className="admin-post-months">
              {monthlyAvailability.map((month) => (
                <label key={month.monthKey} className="admin-month-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedPostMonths.has(month.monthKey)}
                    onChange={() => togglePostMonth(month.monthKey)}
                  />
                  <span>{capitalizeFirst(formatLocalizedMonth(month.monthDate))}</span>
                </label>
              ))}
            </div>

            <textarea className="admin-post-textarea" readOnly value={buildPostText()} rows={14} />

            <div className="admin-modal-actions">
              <button type="button" className="admin-copy-btn" onClick={handleCopyPostText}>
                {postCopyFeedback || 'Copier le texte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;